import { TrophyOutlined, GiftOutlined, LikeOutlined, EyeOutlined, CheckCircleOutlined, CompressOutlined, ExpandOutlined, UploadOutlined } from '@ant-design/icons';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Input, Button, Steps, List, Checkbox, Avatar, Select, message as antMessage, Popover, Spin } from 'antd';
import type { StepsProps } from 'antd';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import AccelerateInnovation from '../../assets/images/CoreValuesImages/AccelerateInnovation.png';
import BeAgile from '../../assets/images/CoreValuesImages/BeAgile.png';
import DriveResults from '../../assets/images/CoreValuesImages/DriveResults.png';
import GrowTogether from '../../assets/images/CoreValuesImages/GrowTogether.png';
import { useTranslation } from 'react-i18next';
import { fetchWithRetry } from '../utils/fetchWithRetry';

const { Step } = Steps;
const { Option } = Select;
const { TextArea } = Input;

// Font used for the stepper labels and step-0 headings.
const APPRECIATE_FONT = "inherit"; // follows the app-wide Inter font

interface AppreciateProps {
  visible: boolean;
  onClose: () => void;
}

// Props for the eCards
interface ECard {
  ecardId: string,
  eCardName: string,
  description: string,
  image: string,
  isActive: boolean,
  isDefault: boolean,
  valueId: string,
  title?: string,
  background?: string;
}

// Props for the recipient
interface Recipient {
  id: string;
  displayName: string;
  email: string;
}


const Appreciate: React.FC<AppreciateProps> = ({ visible, onClose }) => {
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [recipient, setRecipient] = useState<string | null>(null);
  const [nominatorName, setNominatorName] = useState('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [recipientDetails, setRecipientDetails] = useState<Recipient | null>(null);
  const { instance, accounts } = useMsal();
  const [recipientSearchResults, setRecipientSearchResults] = useState<{ id: string, displayName: string, email: string }[]>([]);
  const [recipientSearchResultsSameTeam, setRecipientSearchResultsSameTeam] = useState<{ id: string, displayName: string, email: string }[]>([]);
  const [nominatorEmail, setNominatorEmail] = useState('');
  const [recipientId, setRecipientId] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [description, setDescription] = useState<string>('');
  const [ecards, setEcards] = useState<ECard[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [eCardsLoading, setECardsLoading] = useState<boolean>(false);
  const [selectedEcardData, setSelectedEcardData] = useState<ECard | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isVisibleToSocialWall, setIsVisibleToSocialWall] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isEnlarged, setIsEnlarged] = useState(false);
  const [showCardsModal, setShowCardsModal] = useState(false);
  const [filteredEcards, setFilteredEcards] = useState<{ description: string, ecardId: string, eCardName: string, image: string, isActive: boolean, isDefault: boolean, valueId: string }[]>([]);
  const [cards, setCards] = useState<{ icon: JSX.Element, title: string, background: string, valueId: string }[]>([]);
  const { t } = useTranslation();

  const backgroundColors = useMemo(() => [
    'linear-gradient(238.85deg, #F3E8FF 0%, #E879F9 100%)',
    'linear-gradient(238.85deg, #E0F2FE 0%, #60A5FA 100%)',
    'linear-gradient(238.85deg, #FEF3C7 0%, #FBBF24 100%)',
    'linear-gradient(238.85deg, #DCFCE7 0%, #4ADE80 100%)',
    'linear-gradient(238.85deg, #D1FAE5 0%, #34D399 100%)',
    'linear-gradient(238.85deg, #F3F4F6 0%, #9CA3AF 100%)',
    'linear-gradient(238.85deg, #FEF3C7 0%, #FBBF24 100%)',
    'linear-gradient(238.85deg, #F3F4F6 0%, #9CA3AF 100%)',
  ], []);

  const fetchToken = useCallback(async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  }, [instance, accounts]);

  useEffect(() => {
    fetchToken();
  }, []);

  useEffect(() => {
    const fetchCoreValues = async () => {
      try {
        const token = await fetchToken();
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/CoreValues/ActiveWithIds`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const coreValues = response.data.map((value: { valueId: string, valueName: string }, index: number) => {
          const icons = {
            'Accelerate Innovation': <img src={AccelerateInnovation} alt="Accelerate Innovation" style={{ width: '200px', height: '200px' }} />,
            'Be Agile': <img src={BeAgile} alt="Be Agile" style={{ width: '200px', height: '200px' }} />,
            'Drive Results': <img src={DriveResults} alt="Drive Results" style={{ width: '200px', height: '200px' }} />,
            'Grow Together': <img src={GrowTogether} alt="Grow Together" style={{ width: '200px', height: '200px' }} />
          };

          return {
            icon: icons[value.valueName as keyof typeof icons] || <img src={AccelerateInnovation} alt="Default" style={{ width: '200px', height: '200px' }} />,
            title: value.valueName,
            valueId: value.valueId,
          };
        });
        setCards(coreValues);
      } catch (error) {
        console.error('Error fetching core values:', error);
      }
    };

    fetchCoreValues();
  }, [fetchToken, backgroundColors]);

  useEffect(() => {
    const fetchEcardsByCoreValue = async (coreValueId: string) => {
      setECardsLoading(true);
      try {
        const token = await fetchToken();
        // Add timestamp to prevent caching and force fresh load from database
        const cacheBuster = `?t=${Date.now()}`;
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Ecards/ByCoreValue/${coreValueId}${cacheBuster}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
        const ecardsWithBase64 = response.data.map((ecard: { description: string, ecardId: string, eCardName: string, image: string, isActive: boolean, isDefault: boolean, valueId: string }) => ({
          ecardId: ecard.ecardId,
          image: `data:image/jpeg;base64,${ecard.image}`, // Ensure the base64 data is correctly formatted
          description: ecard.description,
          eCardName: ecard.eCardName,
          isActive: ecard.isActive,
          isDefault: ecard.isDefault,
          valueId: ecard.valueId,
        }));
        setEcards(ecardsWithBase64);
      } catch (error) {
        console.error('Error fetching eCards:', error);
      } finally {
        setECardsLoading(false);
      }
    };

    if (selectedCard) {
      const selectedValueId = cards.find(card => card.title === selectedCard)?.valueId;
      if (selectedValueId) {
        fetchEcardsByCoreValue(selectedValueId);
      }
    }
  }, [selectedCard, cards, fetchToken]);

  // useEffect(() => {
  //   if (selectedCard) {
  //     const selectedValueId = cards.find(card => card.title === selectedCard)?.valueId;
  //     const filtered = ecards.filter(ecard => ecard.valueId === selectedValueId);
  //     setFilteredEcards(filtered);
  //   }
  // }, [selectedCard, ecards, cards]);

  const toggleEnlarge = () => {
    setIsEnlarged(!isEnlarged);
  };

  const handleCheckboxChange = (e: any) => {
    setIsVisibleToSocialWall(e.target.checked);
  };
  // Users Data integration

  useEffect(() => {
    if (currentStep === 1) {
      const fetchTeamMembersData = async () => {
        const employeeId = await fetchEmployeeId();
        fetchTeamMembers(employeeId);
      };
      fetchTeamMembersData();
    }
  }, [currentStep]);

  const callMsGraph = async (token: any) => {
    const headers = new Headers();
    headers.append("Authorization", `Bearer ${token}`);
    const response = await fetchWithRetry(`https://graph.microsoft.com/v1.0/me?`, {
      method: "GET",
      headers,
    }).then((response) => response.json());
    return response;
  };

  const requestProfileData = useCallback(async () => {
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ["User.Read", "User.Read.All"],
        authority: `https://login.microsoftonline.com/560ec2b0-df0c-4e8c-9848-a15718863bb6`,
      });
      const data = await callMsGraph(response.accessToken);
      localStorage.setItem("UserMail", data?.mail.toLowerCase());
      setNominatorName(data?.displayName || 'Default Nominator');
      setNominatorEmail(data?.mail.toLowerCase());
    } catch (error) {
      console.error("Silent token acquisition failed, acquiring token using redirect");
      instance.loginRedirect(loginRequest);
    }
  }, [instance]);

  useEffect(() => {
    requestProfileData();
  }, [requestProfileData]);

  const fetchEmployeeId = async () => {
    try {
      const token = await fetchToken();
      const baseURL = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${baseURL}/api/Users/CheckEmail`, {
        params: { email: nominatorEmail },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.status === 200) {
        // You can access the EmployeeId here
        const employeeId = response.data.userId;
        return employeeId;
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response && error.response.status === 400) {
        console.error('Bad Request:', error.response.data);
      } else if ((error as any).response && (error as any).response.status === 404) {
        console.error('Employee does not exist:', (error as any).response.data);
      } else {
        console.error('Error fetching values:', error);
      }
    }
  };

  useEffect(() => {
    const fetchNomineeData = async () => {
      if (searchTerm.length < 3) {
        setRecipientSearchResults([]);
        return;
      }
      try {
        const token = await fetchToken();
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Users/search?search=${encodeURIComponent(searchTerm)}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setRecipientSearchResults(response.data);
      } catch (error) {
        console.error('Error fetching recipients:', error);
      }
    };

    fetchNomineeData();
  }, [searchTerm]);

  const handleRecipientSelect = async (value: string, option: any) => {
    try {
      const employee = await fetchEmployeeId();

      if (option.key == employee) {
        antMessage.error('You cannot select yourself as a recipient.');
        return;
      }

      // Find the recipient from both search results and team members
      const foundRecipient = [...recipientSearchResults, ...recipientSearchResultsSameTeam]
        .find(item => item.id === option.key);

      if (foundRecipient) {
        setRecipientId(foundRecipient.id);
        setRecipient(foundRecipient.displayName);
        setRecipientDetails(foundRecipient);
        setHasUnsavedChanges(true);
      }

    } catch (error) {
      console.error('Error fetching employee ID:', error);
    }

    // Allow selection to proceed even if validation fails
    const foundRecipient = [...recipientSearchResults, ...recipientSearchResultsSameTeam]
      .find(item => item.id === option.key);

    if (foundRecipient) {
      setRecipientId(foundRecipient.id);
      setRecipient(foundRecipient.displayName);
      setRecipientDetails(foundRecipient);
      setHasUnsavedChanges(true);
    }
  };

  const fetchTeamMembers = async (userId: string) => {
    try {
      const token = await fetchToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Users/SameManager`, {
        params: { userId },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      if (response.status === 200) {
        setRecipientSearchResultsSameTeam(response.data);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const getColorFromName = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xFF;
      color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
  };

  // End of user integration

  const handleCardClick = async (title: string) => {
    setHasUnsavedChanges(true);
    setSelectedCard(title);
    setECardsLoading(true);

    const selectedValueId = cards.find(card => card.title === title)?.valueId;
    if (selectedValueId) {
      try {
        const token = await fetchToken();
        // Add timestamp to prevent caching and force fresh load from database
        const cacheBuster = `?t=${Date.now()}`;
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Ecards/ByCoreValue/${selectedValueId}${cacheBuster}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
        const ecardsWithBase64 = response.data.map((ecard: { description: string, ecardId: string, eCardName: string, image: string, isActive: boolean, isDefault: boolean, valueId: string }) => ({
          ecardId: ecard.ecardId,
          image: `data:image/jpeg;base64,${ecard.image}`, // Ensure the base64 data is correctly formatted
          description: ecard.description,
          eCardName: ecard.eCardName,
          isActive: ecard.isActive,
          isDefault: ecard.isDefault,
          valueId: ecard.valueId,
        }));
        setEcards(ecardsWithBase64);
        if (ecardsWithBase64.length > 0) {
          handleECardSelect(ecardsWithBase64[0].image, ecardsWithBase64[0].ecardId);
        }
      } catch (error) {
        console.error('Error fetching eCards:', error);
      } finally {
        setECardsLoading(false);
      }
    }
    setCurrentStep(1);
  };

  const handleECardSelect = (image: string, imageId: string) => {
    setImage(image);
    setSelectedImageId(imageId);
    setHasUnsavedChanges(true);
    setShowCardsModal(false);
  };

  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const resetForm = () => {
    setSelectedCard(null);
    setCurrentStep(0);
    setRecipient(null);
    setImage(null);
    setSelectedImageId(null);
    setRecipientDetails(null);
    setDescription('');
    setSelectedImage(null);
    setIsFullScreen(false);
    setHasUnsavedChanges(false);
  };

  const handleSubmit = async () => {
    const nominatorId = await fetchEmployeeId(); // Assuming fetchEmployeeId returns the nominator's ID
    const companyValueId = cards.find(card => card.title === selectedCard)?.valueId;

    const data = {
      nominatorId, // Ensure this matches the backend model
      nomineeId: recipientId, // Ensure this matches the backend model
      companyValueId, // Ensure this matches the backend model
      ecard: selectedImageId || null,
      message: description,
      visibility: isVisibleToSocialWall,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    try {
      const token = await fetchToken();
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Appreciations`, data, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.status === 201) {
        const emailData = {
          subject: "Aptean Thanks - Appreciates",
          celebrationId: null,
          appreciationId: response.data.appreciationId
        };
        resetForm();
        onClose();
        antMessage.success(`You have Appreciated ${recipient} for demonstrating ${selectedCard}!`);
        const response1 = await axios.post(`${process.env.REACT_APP_API_URL}/api/EmailNotification/SendEmailAcknowledgement`, emailData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        resetForm();
        onClose();
      } else {
        console.error('Failed to submit appreciation:', response.data);
      }
    } catch (error) {
      console.error('Error submitting appreciation:', error);
    }
  };

  const handleModalClose = () => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to close the form? Your changes will be lost.',
        onOk: () => {
          resetForm();
          onClose();
        },
      });
    } else {
      resetForm();
      onClose();
    }
  };



  const renderCardContent = () => (
    eCardsLoading ? (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0' }}>
        <Spin size="large" tip="Loading images from database..." />
      </div>
    ) : (
    <div style={{ padding: '2px 0 8px', fontFamily: APPRECIATE_FONT }}>
      <style>{`
        .appreciate-value-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          width: 100%;
          max-width: 380px;
          margin: 0 auto;
        }
        @media (max-width: 560px) {
          .appreciate-value-grid { grid-template-columns: 1fr; max-width: 220px; }
        }
        .appreciate-value-card {
          position: relative;
          background: #ffffff;
          border: 1.5px solid #ececf1;
          border-radius: 12px;
          padding: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
          overflow: hidden;
        }
        .appreciate-value-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(134,41,164,0.15);
          border-color: #d9b8e6;
        }
        .appreciate-value-card.selected {
          border-color: #8629A4;
          box-shadow: 0 0 0 3px rgba(134,41,164,0.18), 0 8px 20px rgba(134,41,164,0.18);
        }
        .appreciate-value-card img {
          width: 100% !important;
          height: auto !important;
          max-width: 150px;
          border-radius: 9px;
          display: block;
        }
        .appreciate-value-check {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #8629A4;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 700;
          box-shadow: 0 2px 6px rgba(0,0,0,0.25);
        }
      `}</style>

      <div style={{ textAlign: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '15px', fontWeight: 600, color: '#1a1a1a' }}>
          {t('select_values')}
        </div>
        <div style={{ fontSize: '12.5px', color: '#8c8c8c', marginTop: '2px' }}>
          Choose the Aptean Core Value you want to recognise
        </div>
      </div>

      <div className="appreciate-value-grid">
        {cards.map((card, index) => {
          const isSelected = selectedCard === card.title;
          return (
            <div
              key={index}
              className={`appreciate-value-card${isSelected ? ' selected' : ''}`}
              onClick={() => handleCardClick(card.title)}
              role="button"
              aria-pressed={isSelected}
              title={card.title}
            >
              {card.icon}
              {isSelected && <span className="appreciate-value-check">✓</span>}
            </div>
          );
        })}
      </div>
    </div>
    )
  );

  const renderRecipientSelection = () => {
    const combinedResults = [...recipientSearchResults, ...recipientSearchResultsSameTeam];
    const uniqueResults = Array.from(new Set(combinedResults.map(item => item.id)))
      .map(id => combinedResults.find(item => item.id === id));

    return (
      <div style={{ display: 'flex', justifyContent: "center", gap: '16px', alignItems: 'flex-start', WebkitJustifyContent: 'space-evenly', maxWidth: '100%', height: '78%' }}>
        <div style={{ width: '40%', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <p
            style={{
              color: 'rgba(0, 0, 0, 0.65)',
              fontFamily: 'inherit',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '22px',
            }}
          >
            <span style={{ color: 'red' }}>*</span>
            {t('select_recipient')}
          </p>
          <Select
            showSearch
            placeholder="Search nominee"
            value={recipient}
            onSearch={setSearchTerm}
            onChange={handleRecipientSelect}
            filterOption={true}
            style={{ flex: 1, maxWidth: '350px', marginBottom: '16px' }}
          >
            {uniqueResults.map((item) => item && (
              <Option key={item.id} value={item.displayName} label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar style={{ marginRight: '8px', backgroundColor: getColorFromName(item.displayName) }}>{item.displayName.charAt(0)}</Avatar>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{item.displayName}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{item.email}</div>
                  </div>
                </div>
              }>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar style={{ marginRight: '8px', backgroundColor: getColorFromName(item.displayName) }}>{item.displayName.charAt(0)}</Avatar>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{item.displayName}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{item.email}</div>
                  </div>
                </div>
              </Option>
            ))}
          </Select>
        </div>

        <div style={{ width: '0.5px', height: '391px', background: 'var(--Colors-Neutral-Border-colorBorder, #D9D9D9)' }}></div>
        <div>
          <p
            style={{
              color: 'rgba(0, 0, 0, 0.65)',
              fontFamily: 'inherit',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '22px',
            }}
          >
            {t('suggested_members')}
          </p>
          <List
            style={{ flex: 1, justifyContent: "center", maxWidth: '300px', height: '300px', overflowY: 'auto', backgroundColor: 'white' }}
            dataSource={recipientSearchResultsSameTeam}
            renderItem={(item) => item && (
              <List.Item
                onClick={() => handleRecipientSelect(item.displayName, { key: item.id })}
                style={{
                  maxWidth: '500px',
                  justifyContent: "flex-start",
                  cursor: 'pointer',
                  backgroundColor: recipient === item.displayName ? '#e6f7ff' : 'white',
                  border: recipient === item.displayName ? '1px solid #1890ff' : '1px solid #d9d9d9',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px',
                  marginBottom: '4px', // Add margin between items
                  borderRadius: '8px', // Add border radius for better appearance
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', // Add shadow for better appearance
                }}
              >
                <Avatar style={{ marginRight: '8px', backgroundColor: getColorFromName(item.displayName) }}>{item.displayName.charAt(0)}</Avatar>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{item.displayName}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>{item.email}</div>
                </div>
              </List.Item>
            )}
          />
        </div>
      </div>
    );
  };

  const renderImageUpload = () => {
    // Maximum number of cards to display initially
    const MAX_VISIBLE_CARDS = 3;

    // Handler to select an eCard
    const handleECardSelect = (image: string, imageId: string) => {
      // same logic as before
      setImage(image);
      setSelectedImageId(imageId);
      setHasUnsavedChanges(true);
      // close any "all cards" modal if open
      setShowCardsModal(false);
    };

    // Handler to open "All Cards" modal
    const handleViewAllCards = () => {
      setShowCardsModal(true);
    };

    return (
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          padding: "8px 24px 0",
          fontFamily: 'inherit',
        }}
      >
        {/* The "Message" textarea */}
        <div>
          <p
            style={{
              color: "rgba(0, 0, 0, 0.85)",
              fontSize: "14px",
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            <span style={{ color: "red" }}>*</span>
            {t('message')}
          </p>
          <TextArea
            placeholder={t('write_message_here')}
            value={description}
            maxLength={1000}
            onChange={(e) => {
              setDescription(e.target.value.replace(/[^a-zA-Z0-9 .,\-!\n]/g, ''));
              setHasUnsavedChanges(true);
            }}
            rows={4}
            style={{
              fontSize: "14px",
              resize: "none",
              marginBottom: "2px",
              color: "#000000",
              fontWeight: "normal",
            }}
          />
          <div
            style={{
              color: "#888",
              fontSize: "12px",
              marginTop: "4px",
              marginBottom: "12px",
            }}
          >
            {/* Optional helper text */}
          </div>
        </div>

        {/* eCard selection grid */}
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <p
              style={{
                color: "rgba(0, 0, 0, 0.85)",
                fontSize: "14px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              <span style={{ color: "red" }}>*</span>
              {t('ecard')}
            </p>
            {/* <Button icon={<UploadOutlined />} style={{ fontSize: "14px" }}>
              Upload Photo
            </Button> */}
          </div>

          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
              <Spin size="large" />
            </div>
          ) : (
            <div>
              {/* If there is a selected eCard, display it */}
              {selectedImageId && (
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ position: "relative", width: "280px", margin: "0 auto" }}>
                    {ecards
                      .filter(ecard => ecard.ecardId === selectedImageId)
                      .map(ecard => (
                        <div
                          key={ecard.ecardId}
                          style={{
                            height: "120px",
                            width: "242px",
                            borderRadius: "8px",
                            background: ecard.background || undefined,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "12px",
                            border: "3px solid #1890ff",
                            overflow: "hidden",
                            boxShadow: "0 4px 8px rgba(24, 144, 255, 0.2)",
                            transition: "all 0.2s ease-in-out",
                            position: "relative",
                          }}
                          onClick={handleViewAllCards}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(24, 144, 255, 0.05)",
                              pointerEvents: "none",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "#1890ff",
                              color: "white",
                              padding: "2px 6px",
                              borderRadius: "10px",
                              fontSize: "12px",
                              fontWeight: "bold",
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                              zIndex: 3,
                            }}
                          >
                            ✓ Selected
                          </div>

                          {/* If this eCard is not a default background eCard, show the image */}
                          {ecard.background ? (
                            <div
                              style={{
                                background: "white",
                                padding: "6px 12px",
                                borderRadius: "20px",
                                fontSize: "14px",
                                fontWeight: "500",
                                zIndex: 1,
                              }}
                            >
                              {ecard.title || "eCard"}
                            </div>
                          ) : (
                            <div style={{ position: "relative", width: "100%", height: "100%" }}>
                              <img
                                src={ecard.image}
                                alt="eCard"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: "4px",
                                }}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  top: "3px",
                                  left: "4px",
                                  display: "flex",
                                  gap: "5px",
                                  zIndex: 2,
                                }}
                              >
                                <div
                                  style={{
                                    width: "26px",
                                    height: "26px",
                                    borderRadius: "50%",
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewImage(ecard.image);
                                  }}
                                >
                                  <EyeOutlined style={{ color: "#1890ff", fontSize: "18px" }} />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                  {/* <Button
                    type="link"
                    onClick={handleViewAllCards}
                    style={{
                      display: "block",
                      margin: "8px auto",
                      padding: "0",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Change eCard
                  </Button> */}
                </div>
              )}

              {/* Only show card grid if no eCard has been selected */}
              {!selectedImageId && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "16px",
                    marginBottom: "16px",
                  }}
                >
                  {ecards.slice(0, MAX_VISIBLE_CARDS - 1).map((ecard) => (
                    <div
                      key={ecard.ecardId}
                      style={{
                        height: "120px",
                        borderRadius: "8px",
                        background: ecard.background || undefined,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "12px",
                        border: "1px solid #d9d9d9",
                        overflow: "hidden",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                        transition: "all 0.2s ease-in-out",
                        position: "relative",
                      }}
                      onClick={() => handleECardSelect(ecard.image, ecard.ecardId)}
                    >
                      {ecard.background ? (
                        <div
                          style={{
                            background: "white",
                            padding: "6px 12px",
                            borderRadius: "20px",
                            fontSize: "14px",
                            fontWeight: "500",
                            zIndex: 1,
                          }}
                        >
                          {ecard.title || "eCard"}
                        </div>
                      ) : (
                        <div style={{ position: "relative", width: "100%", height: "100%" }}>
                          <img
                            src={ecard.image}
                            alt="eCard"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: "5px",
                              right: "5px",
                              display: "flex",
                              gap: "5px",
                              zIndex: 2,
                            }}
                          >
                            <div
                              style={{
                                width: "26px",
                                height: "26px",
                                borderRadius: "50%",
                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                opacity: 0,
                                transition: "opacity 0.2s ease-in-out",
                              }}
                              className="eye-icon-container"
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = "1";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = "0";
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewImage(ecard.image);
                              }}
                            >
                              <EyeOutlined style={{ color: "#1890ff", fontSize: "18px" }} />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* "Show more" card (only if there are more than MAX_VISIBLE_CARDS cards) */}
                  {ecards.length > 2 && (
                    <div
                      style={{
                        height: "120px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "12px",
                        border: "1px solid #d9d9d9",
                        overflow: "hidden",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                        position: "relative",
                        background: ecards.length > 2 && !ecards[2].background
                          ? "white"
                          : (ecards[2]?.background || "white"),
                      }}
                      onClick={handleViewAllCards}
                    >
                      {ecards.length > 2 && !ecards[2].background && (
                        <img
                          src={ecards[2].image}
                          alt="eCard Preview"
                          style={{
                            position: "absolute",
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            filter: "blur(5px)",
                            opacity: 0.7,
                          }}
                        />
                      )}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "white",
                          zIndex: 2,
                        }}
                      >
                        <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                          + {ecards.length - 2} More
                        </span>
                        <span style={{ fontSize: "14px", marginTop: "4px" }}>
                          View All
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Full-Screen Preview Modal */}
        {selectedImage && (
          <Modal
            visible={isFullScreen}
            footer={null}
            onCancel={() => setIsFullScreen(false)}
            width="100%"
            style={{ top: 0, height: "100vh" }}
            bodyStyle={{ height: "calc(100vh - 55px)", overflow: "auto" }}
          >
            <img
              src={selectedImage}
              alt="Full Screen"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </Modal>
        )}

        {/* All eCards Modal */}
        <Modal
          title="Select an eCard"
          visible={showCardsModal}
          onCancel={() => setShowCardsModal(false)}
          footer={null}
          width={800}
          centered
          bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "16px",
              padding: "16px",
            }}
          >
            {ecards.map((ecard) => {
              const isThisCardSelected = selectedImageId === ecard.ecardId;
              return (
                <div
                  key={ecard.ecardId}
                  style={{
                    height: "120px",
                    borderRadius: "8px",
                    background: ecard.background || undefined,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "12px",
                    border: isThisCardSelected ? "3px solid #1890ff" : "1px solid #d9d9d9",
                    overflow: "hidden",
                    boxShadow: isThisCardSelected
                      ? "0 4px 8px rgba(24, 144, 255, 0.2)"
                      : "0 2px 4px rgba(0, 0, 0, 0.1)",
                    position: "relative",
                  }}
                  onClick={() => handleECardSelect(ecard.image, ecard.ecardId)}
                >
                  {isThisCardSelected && (
                    <div
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        backgroundColor: "#1890ff",
                        color: "white",
                        padding: "2px 6px",
                        borderRadius: "10px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                        zIndex: 3,
                      }}
                    >
                      ✓ Selected
                    </div>
                  )}
                  {ecard.background ? (
                    <div
                      style={{
                        background: "white",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        fontSize: "14px",
                        fontWeight: "500",
                        zIndex: 1,
                      }}
                    >
                      {ecard.title || "eCard"}
                    </div>
                  ) : (
                    <img
                      src={ecard.image}
                      alt="eCard"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </Modal>
      </div>
    );
  };

  const handleImageSelect = (image: string, imageId: string) => {
    setImage(image);
    setSelectedImageId(imageId);
    setHasUnsavedChanges(true);
  };

  const handleViewImage = (image: string) => {
    setSelectedImage(image);
    setIsFullScreen(true);
  };

  const handleImageUpload = (info: any) => {
    if (info.file && info.file.originFileObj) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result as string);
      };
      reader.readAsDataURL(info.file.originFileObj);
    }
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  // Custom dots for Steps component

  const renderReview = () => {
    // We'll show a similar preview of the selected eCard, message, etc.

    // Helper function to open the "All Cards" modal from the preview
    const handleECardClick = () => {
      setShowCardsModal(true);
    };

    // If the user selects a new card from the preview modal
    const handleECardSelect = (image: string, imageId: string) => {
      const selectedEcard = ecards.find(e => e.ecardId === imageId);
      if (selectedEcard) {
        setImage(image);
        setSelectedImageId(imageId);
        setHasUnsavedChanges(true);
      }
    };

    // We'll replicate the same "View All eCards" logic, so the same showCardsModal is used.
    return (
      <div
        style={{
          maxHeight: "400px",
          overflowY: "auto",
          padding: "8px 24px 32px",
          borderRadius: "8px",
          fontFamily: 'inherit',
          marginBottom: '18px'
        }}
      >
        {/* Category - i.e., the selected Aptean Core Value */}
        <div style={{ marginBottom: "16px" }}>
          <p
            style={{
              color: 'rgba(0, 0, 0, 0.85)',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '8px'
            }}
          >
            {t('selected_core_values')}
          </p>
          {selectedCard && (
            <div
              style={{
                display: 'inline-flex',
                padding: '4px 12px',
                borderRadius: '16px',
                background: 'beige',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '14px',
              }}
              onClick={() => setCurrentStep(0)}
            >
              <span style={{ fontWeight: '500' }}>{selectedCard}</span>
            </div>
          )}
        </div>

        {/* Recipient */}
        <div style={{ marginBottom: "16px" }}>
          <p
            style={{
              color: 'rgba(0, 0, 0, 0.85)',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '8px'
            }}
          >
            {t('selected_recipient')}
          </p>
          {recipientDetails && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                border: '1px solid #d9d9d9',
                borderRadius: '8px',
                width: 'fit-content',
                cursor: 'pointer',
                fontSize: '14px',
                background: '#E0F7FA',
              }}
              onClick={() => setCurrentStep(selectedCard === 'Festivals' ? 2 : 1)}
            >
              <Avatar style={{ marginRight: '8px', backgroundColor: getColorFromName(recipientDetails.displayName) }}>
                {recipientDetails.displayName.charAt(0)}
              </Avatar>
              <div>
                <div style={{ fontWeight: '500' }}>{recipientDetails.displayName}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>{recipientDetails.email}</div>
              </div>
            </div>
          )}
        </div>

        {/* Message */}
        <div style={{ marginBottom: "24px" }}>
          <p
            style={{
              color: 'rgba(0, 0, 0, 0.85)',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '8px'
            }}
          >
            {t('your_message')}
          </p>
          <TextArea
            value={description}
            rows={3}
            readOnly
            style={{
              resize: 'none',
              fontSize: '14px',
              fontFamily: 'inherit',
              cursor: 'pointer'
            }}
            onClick={() => setCurrentStep(2)}
          />
        </div>

        {/* Selected eCard */}
        <div style={{ paddingBottom: '24px' }}>
          <p
            style={{
              color: 'rgba(0, 0, 0, 0.85)',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '8px'
            }}
          >
            {t('selected_ecard')}
          </p>
          {selectedImageId && (
            <div
              style={{
                height: '120px',
                width: '240px',
                borderRadius: '8px',
                border: '1px solid #d9d9d9',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                marginBottom: '16px',
                position: 'relative',
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
              onClick={handleECardClick}
            >
              {ecards
                .filter(ecard => ecard.ecardId === selectedImageId)
                .map(ecard => (
                  <React.Fragment key={ecard.ecardId}>
                    {ecard.background ? (
                      <div
                        style={{
                          background: "white",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "14px",
                          fontWeight: "500",
                          zIndex: 1,
                        }}
                      >
                        {ecard.title || "eCard"}
                      </div>
                    ) : (
                      <img
                        src={ecard.image}
                        alt="Selected eCard"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
            </div>
          )}
        </div>

        {/* Visibility checkbox */}
        <Checkbox
          checked={isVisibleToSocialWall}
          onChange={handleCheckboxChange}
          style={{ marginBottom: '15px' }}
        >
          {t('share_feeds')}
        </Checkbox>
        
      </div>
    );
  };

  const customDot: StepsProps['progressDot'] = (dot, { status, index }) => (
    <Popover
      content={
        <span>
          {
            (index === 0 ? 'Select Aptean Core Value' :
              index === 1 ? 'Choose who you want to appreciate' :
                index === 2 ? 'Write your message and pick an eCard' :
                  'Review your appreciation details')
          }
        </span>
      }
    >
      {dot}
    </Popover>
  );

  const renderSteps = () => {
    // 4-step flow

    return (
      <div>
        <style>
          {`
                @media (max-width: 768px) {
                  .hide-on-mobile {
                    display: none;
                  }
                }
                /* Brand-purple stepper (default antd is blue) */
                .appreciate-steps .ant-steps-item-finish .ant-steps-icon-dot,
                .appreciate-steps .ant-steps-item-process .ant-steps-icon-dot {
                  background: #8629A4 !important;
                }
                .appreciate-steps .ant-steps-item-process .ant-steps-item-title {
                  color: #8629A4 !important;
                  font-weight: 600 !important;
                }
                .appreciate-steps .ant-steps-item-finish .ant-steps-item-tail::after {
                  background-color: #8629A4 !important;
                }
              `}
        </style>
        <div className="hide-on-mobile">
          {/* Internal CSS */}
          <Steps
            className="appreciate-steps"
            current={currentStep}
            style={{ marginBottom: '4px', paddingTop: '12px' }}
            progressDot={customDot}
            items={[
              { title: <span style={{ fontSize: '13.5px', fontFamily: APPRECIATE_FONT, fontWeight: 500 }}>{t('select_values')}</span> },
              { title: <span style={{ fontSize: '13.5px', fontFamily: APPRECIATE_FONT, fontWeight: 500 }}>{t('select_recipient')}</span> },
              { title: <span style={{ fontSize: '13.5px', fontFamily: APPRECIATE_FONT, fontWeight: 500 }}>{t('message_ecard')}</span> },
              { title: <span style={{ fontSize: '13.5px', fontFamily: APPRECIATE_FONT, fontWeight: 500 }}>{t('preview')}</span> }
            ]}
          />
        </div>
      </div>
    );

  };

  const renderContent = () => {
    switch (currentStep) {
      case 0:
        return renderCardContent();
      case 1:
        return renderRecipientSelection();
      case 2:
        return renderImageUpload();
      case 3:
        return renderReview();
      default:
        return renderCardContent();
    }
  };

  return (
    <Modal
      title={<span style={{
        fontFamily: 'inherit',
        fontSize: '18px',
        fontWeight: 'bold'
      }}>{t('appreciate')}</span>}
      visible={visible}
      onCancel={handleModalClose}
      footer={null}
      centered
      width={800}
      bodyStyle={{
        maxHeight: 'calc(85vh - 55px)',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        overflow: 'hidden',
        fontFamily: 'inherit',
        fontSize: '14px',
      }}
    >
      <div style={{ padding: '16px 16px 0', flexShrink: 0 }}>
        {renderSteps()}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
        {renderContent()}
      </div>

      <div style={{
        flexShrink: 0,
        borderTop: '1px solid #f0f0f0',
        background: 'white',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
      }}>

        <div>
          {currentStep > 0 && (
            <Button
              onClick={handlePreviousStep}
              size='middle'
              style={{ fontSize: '16px', minWidth: '100px', height: '40px' }}
            >
              {t('back')}
            </Button>
          )}
        </div>

        <div>
          {currentStep > 0 && currentStep < 3 && ( // Remove "Next" button for core values step
            <Button
              type="primary"
              onClick={handleNextStep}
              size="middle"
              style={{ fontSize: '16px', minWidth: '100px', height: '40px', backgroundColor: '#8629A4' }}
              disabled={
                (currentStep === 1 && !recipient) ||
                (currentStep === 2 && (!description || !selectedImageId))
              }
            >
              {currentStep === 2 && description ? t('preview') : t('next')}
            </Button>
          )}
        </div>

        {currentStep === 3 && (
          <Button
            type="primary"
            onClick={handleSubmit}
            size="large"
            style={{ fontSize: '16px', minWidth: '100px', height: '40px', backgroundColor: '#8629A4' }}
            disabled={!selectedCard || !recipient || !description || !selectedImageId}
          >
            {t('submit')}
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default Appreciate;